// EnhancedTopbar - Metronic header layout
// Sol: Logo + NavLinks | Sag: AskAI + FlzHub + Search + CreateProject + Profile + Theme
import { BrainCircuit, Menu, Plus, Sparkles, X } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import NavLinks from './NavLinks'
import ProfileDropdown from './ProfileDropdown'
import ThemeToggle from './ThemeToggle'

export default function EnhancedTopbar() {
  const { user, isAuthenticated } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        background: 'var(--card-bg)',
        borderBottom: '1px solid var(--border-default)',
        height: 'var(--topbar-height)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '100%',
          padding: '0 20px',
          gap: 16,
        }}
      >
        {/* Sol - Logo + NavLinks */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          {/* Logo */}
          <Link
            to="/"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              textDecoration: 'none',
              color: 'var(--text-primary)',
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 700,
                fontSize: 16,
              }}
            >
              R
            </div>
            <span style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-heading)' }}>
              RunAI
            </span>
          </Link>

          {/* NavLinks - Desktop */}
          <div style={{ display: 'flex', gap: 8 }} className="desktop-nav">
            <NavLinks />
          </div>
        </div>

        {/* Sag - Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* AskAI Button */}
          <button
            onClick={() => window.location.href = '/ask-ai'}
            title="Ask AI"
            aria-label="Ask AI"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              borderRadius: 6,
              border: '1px solid var(--border-default)',
              background: 'var(--card-bg)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              transition: 'all .2s',
              fontSize: 13,
              fontWeight: 500,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--hover-bg)'
              e.currentTarget.style.color = 'var(--accent-blue)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--card-bg)'
              e.currentTarget.style.color = 'var(--text-secondary)'
            }}
          >
            <Sparkles size={16} />
            <span className="desktop-only">Ask AI</span>
          </button>

          {/* FlzHub Button - sadece admin */}
          {user?.role === 'admin' && (
          <button
            onClick={() => window.location.href = '/flzhub/chat'}
            title="FlzHub"
            aria-label="FlzHub"
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              borderRadius: 6,
              border: '1px solid var(--border-default)',
              background: 'var(--card-bg)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              transition: 'all .2s',
              fontSize: 13,
              fontWeight: 500,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--hover-bg)'
              e.currentTarget.style.color = 'var(--yellow)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--card-bg)'
              e.currentTarget.style.color = 'var(--text-secondary)'
            }}
          >
            <BrainCircuit size={16} />
            <span className="desktop-only">FlzHub</span>
          </button>
          )}

          {/* CreateProject Button - sadece giris yapilmissa */}
          {isAuthenticated && (
            <button
              onClick={() => window.location.href = '/create-project'}
              title="Create Project"
              aria-label="Create Project"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                borderRadius: 6,
                border: 'none',
                background: 'var(--primary-blue)',
                color: '#fff',
                cursor: 'pointer',
                transition: 'all .2s',
                fontSize: 13,
                fontWeight: 600,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#1d4ed8'
                e.currentTarget.style.transform = 'translateY(-1px)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--primary-blue)'
                e.currentTarget.style.transform = 'translateY(0)'
              }}
            >
              <Plus size={16} />
              <span className="desktop-only">Create Project</span>
            </button>
          )}

          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Profile Dropdown veya Login */}
          {isAuthenticated ? (
            <ProfileDropdown user={user as any} />
          ) : (
            <Link
              to="/login"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 16px',
                borderRadius: 6,
                border: '1px solid var(--border-default)',
                background: 'var(--card-bg)',
                color: 'var(--text-primary)',
                textDecoration: 'none',
                fontSize: 13,
                fontWeight: 500,
                transition: 'all .2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--hover-bg)'
                e.currentTarget.style.borderColor = 'var(--primary-blue)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--card-bg)'
                e.currentTarget.style.borderColor = 'var(--border-default)'
              }}
            >
              Sign In
            </Link>
          )}

          {/* Hamburger Menu - Mobile */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="nav-hamburger"
            aria-label="Toggle menu"
            style={{
              width: 36,
              height: 36,
              borderRadius: 6,
              border: '1px solid var(--border-default)',
              background: 'var(--card-bg)',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'var(--topbar-height)',
            left: 0,
            right: 0,
            background: 'var(--card-bg)',
            borderBottom: '1px solid var(--border-default)',
            padding: '12px 20px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          <NavLinks mobile />
        </div>
      )}
    </div>
  )
}
